<template>
    <div class="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div class="flex flex-wrap items-center justify-between gap-2 px-5 py-3">
            <div class="flex items-center gap-3">
                <h3 class="text-sm font-medium text-gray-900 dark:text-white">
                    Risco de Chargeback
                </h3>

                <span
                    v-if="loading"
                    class="text-xs text-gray-400 dark:text-gray-500"
                >
                    calculando...
                </span>

                <template v-else-if="risk">
                    <span
                        class="rounded-full px-2 py-0.5 text-xs font-semibold"
                        :class="levelBadgeClass"
                    >
                        {{ levelLabel }}
                    </span>

                    <span
                        v-if="risk.exit"
                        class="rounded-full px-2 py-0.5 text-xs font-semibold"
                        :class="exitBadgeClass"
                    >
                        {{ exitLabel }}
                    </span>

                    <span
                        v-if="risk.tail && risk.tail.level !== 'normal'"
                        class="rounded-full px-2 py-0.5 text-xs font-semibold"
                        :class="tailBadgeClass"
                    >
                        {{ tailLabel }}
                    </span>

                    <span
                        v-if="risk.flags && risk.flags.global_watchlist"
                        class="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700 dark:bg-orange-900/40 dark:text-orange-300"
                    >
                        Watchlist 12m
                    </span>

                    <span
                        v-if="risk.penalty < 0"
                        class="font-mono text-xs text-gray-500 dark:text-gray-400"
                    >
                        penalidade v2: {{ risk.penalty }}
                    </span>
                </template>

                <span
                    v-else-if="error"
                    class="text-xs text-gray-400 dark:text-gray-500"
                >
                    indisponível
                </span>
            </div>
        </div>

        <div
            v-if="risk && !loading && risk.combined_alert"
            class="flex items-center gap-2 border-t border-red-200 bg-red-50 px-5 py-2.5 dark:border-red-900/50 dark:bg-red-900/20"
        >
            <span class="h-2 w-2 shrink-0 rounded-full bg-red-500"></span>
            <p class="text-xs font-semibold text-red-700 dark:text-red-300">
                Exposição de chargeback com sinais de saída: o vendedor está desacelerando as vendas enquanto ainda há caixa recente em disputa potencial. Priorizar análise manual antes de liberar.
            </p>
        </div>

        <div
            v-if="risk && !loading && risk.tail_alert"
            class="flex items-center gap-2 border-t border-red-200 bg-red-50 px-5 py-2.5 dark:border-red-900/50 dark:bg-red-900/20"
        >
            <span class="h-2 w-2 shrink-0 rounded-full bg-red-500"></span>
            <p class="text-xs font-semibold text-red-700 dark:text-red-300">
                Cauda final: sem venda há {{ risk.tail.days_since_last_sale }} dias com prazo D+{{ risk.tail.realized_days }}. O saldo atual é o resto final (nada novo entra) e disputas do cartão 90d ainda podem chegar depois do dreno. Avaliar retenção antes de liberar.
            </p>
        </div>

        <div
            v-if="risk && !loading"
            class="grid grid-cols-2 gap-x-6 gap-y-2 border-t border-gray-100 px-5 py-3 sm:grid-cols-4 dark:border-gray-700/50"
        >
            <div>
                <p class="text-xs text-gray-500 dark:text-gray-400">Caixa 30d</p>
                <p class="font-mono text-sm font-semibold" :class="metricClass(risk.exposure.cb_box_30d, 5, 3)">
                    {{ formatPercent(risk.exposure.cb_box_30d) }}
                </p>
            </div>
            <div>
                <p class="text-xs text-gray-500 dark:text-gray-400">Taxa 90d</p>
                <p class="font-mono text-sm font-semibold" :class="metricClass(risk.exposure.cb_rate_90d, 2.5, 1)">
                    {{ formatPercent(risk.exposure.cb_rate_90d) }}
                </p>
            </div>
            <div>
                <p class="text-xs text-gray-500 dark:text-gray-400">Saque médio</p>
                <p class="font-mono text-sm font-semibold text-gray-900 dark:text-white">
                    D+{{ risk.exposure.realized_cashout_days }}
                </p>
            </div>
            <div>
                <p class="text-xs text-gray-500 dark:text-gray-400">Global 12m</p>
                <p class="font-mono text-sm font-semibold" :class="metricClass(risk.exposure.cb_global_12m, 2.5, 1)">
                    {{ formatPercent(risk.exposure.cb_global_12m) }}
                </p>
            </div>
        </div>

        <div
            v-if="risk && !loading && risk.exit"
            class="grid grid-cols-2 gap-x-6 gap-y-2 border-t border-gray-100 px-5 py-3 sm:grid-cols-4 dark:border-gray-700/50"
        >
            <div>
                <p class="text-xs text-gray-500 dark:text-gray-400">Vendas 30d</p>
                <p class="font-mono text-sm font-semibold text-gray-900 dark:text-white">
                    {{ formatCurrency(risk.exit.sales_30d_amount) }}
                    <span
                        v-if="risk.exit.drop_pct !== null && risk.exit.drop_pct > 0"
                        class="ml-1 text-xs font-semibold"
                        :class="risk.exit.drop_pct >= 50 ? 'text-red-600 dark:text-red-400' : 'text-yellow-600 dark:text-yellow-400'"
                    >
                        ▼{{ risk.exit.drop_pct }}%
                    </span>
                </p>
            </div>
            <div>
                <p class="text-xs text-gray-500 dark:text-gray-400">30d anteriores</p>
                <p class="font-mono text-sm font-semibold text-gray-900 dark:text-white">
                    {{ formatCurrency(risk.exit.sales_prev_30d_amount) }}
                </p>
            </div>
            <div>
                <p class="text-xs text-gray-500 dark:text-gray-400">Última venda</p>
                <p
                    class="font-mono text-sm font-semibold"
                    :class="risk.exit.days_since_last_sale === null ? 'text-gray-900 dark:text-white' : metricClass(risk.exit.days_since_last_sale, 21, 7)"
                >
                    {{ lastSaleLabel }}
                </p>
            </div>
            <div>
                <p class="text-xs text-gray-500 dark:text-gray-400">Sacado 30d</p>
                <p
                    class="font-mono text-sm font-semibold"
                    :class="risk.exit.withdrawn_30d_amount > risk.exit.sales_30d_amount ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'"
                >
                    {{ formatCurrency(risk.exit.withdrawn_30d_amount) }}
                </p>
            </div>
        </div>

        <div
            v-if="risk && !loading && risk.coverage"
            class="grid grid-cols-2 gap-x-6 gap-y-2 border-t border-gray-100 px-5 py-3 sm:grid-cols-4 dark:border-gray-700/50"
        >
            <div>
                <p class="text-xs text-gray-500 dark:text-gray-400">Este saque</p>
                <p class="font-mono text-sm font-semibold text-gray-900 dark:text-white">
                    {{ formatCurrency(risk.coverage.cashout_amount) }}
                </p>
            </div>
            <div>
                <p class="text-xs text-gray-500 dark:text-gray-400">Fica na conta se liberar</p>
                <p
                    class="font-mono text-sm font-semibold"
                    :class="risk.coverage.retained_after_cashout <= 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'"
                >
                    {{ formatCurrency(risk.coverage.retained_after_cashout) }}
                </p>
            </div>
            <div>
                <p class="text-xs text-gray-500 dark:text-gray-400">Cartão 90d (janela de disputa)</p>
                <p class="font-mono text-sm font-semibold text-gray-900 dark:text-white">
                    {{ formatCurrency(risk.coverage.card_90d_amount) }}
                </p>
            </div>
            <div>
                <p class="text-xs text-gray-500 dark:text-gray-400">Cobertura pós-saque</p>
                <p
                    class="font-mono text-sm font-semibold"
                    :class="coverageClass"
                >
                    {{ risk.coverage.coverage_pct === null ? "n/a" : `${risk.coverage.coverage_pct}%` }}
                </p>
            </div>
        </div>

        <p
            v-if="risk && !loading && risk.level === 'sem_base'"
            class="border-t border-gray-100 px-5 py-2 text-xs text-gray-500 dark:border-gray-700/50 dark:text-gray-400"
        >
            Volume dos últimos 90 dias abaixo do mínimo: taxa de chargeback sem base estatística. Em caso de disputa, avaliar manualmente.
        </p>
    </div>
</template>

<script>
import axios from "axios";

export default {
    name: "ChargebackRiskBadge",
    props: {
        cashoutId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            error: false,
            risk: null,
        };
    },
    computed: {
        levelBadgeClass() {
            const classes = {
                alto: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
                medio: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300",
                baixo: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
                sem_base: "bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300",
            };
            return classes[this.risk?.level] || classes.sem_base;
        },
        levelLabel() {
            const labels = {
                alto: "Risco alto",
                medio: "Risco médio",
                baixo: "Risco baixo",
                sem_base: "Sem base",
            };
            return labels[this.risk?.level] || "Sem base";
        },
        exitBadgeClass() {
            const classes = {
                saindo: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
                esfriando: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300",
                ativo: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
                sem_historico: "bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300",
            };
            return classes[this.risk?.exit?.level] || classes.sem_historico;
        },
        exitLabel() {
            const labels = {
                saindo: "Saindo",
                esfriando: "Esfriando",
                ativo: "Vendas ativas",
                sem_historico: "Sem histórico de vendas",
            };
            return labels[this.risk?.exit?.level] || "Sem histórico de vendas";
        },
        tailBadgeClass() {
            const classes = {
                cauda_final: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
                entrando_na_cauda: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300",
            };
            return classes[this.risk?.tail?.level] || "bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300";
        },
        tailLabel() {
            const tail = this.risk?.tail;

            if (!tail) {
                return "";
            }

            if (tail.level === "cauda_final") {
                return "Cauda final";
            }

            return `Entrando na cauda (esvazia em ~${tail.drains_in_days}d)`;
        },
        coverageClass() {
            const pct = this.risk?.coverage?.coverage_pct;

            if (pct === null || pct === undefined) {
                return "text-gray-900 dark:text-white";
            }

            if (pct < 10) {
                return "text-red-600 dark:text-red-400";
            }

            if (pct < 25) {
                return "text-yellow-600 dark:text-yellow-400";
            }

            return "text-green-600 dark:text-green-400";
        },
        lastSaleLabel() {
            const days = this.risk?.exit?.days_since_last_sale;

            if (days === null || days === undefined) {
                return "nunca";
            }

            if (days === 0) {
                return "hoje";
            }

            return `há ${days}d`;
        },
    },
    watch: {
        cashoutId: {
            immediate: true,
            handler() {
                this.fetchRisk();
            },
        },
    },
    methods: {
        async fetchRisk() {
            if (!this.cashoutId) {
                return;
            }

            this.loading = true;
            this.error = false;
            this.risk = null;

            try {
                const response = await axios.get(
                    `/api/cashout/${this.cashoutId}/review/chargeback-risk`,
                );
                this.risk = response.data.data;
            } catch (error) {
                console.error("Erro ao carregar risco de chargeback:", error);
                this.error = true;
            } finally {
                this.loading = false;
            }
        },
        formatPercent(value) {
            return `${Number(value || 0).toFixed(2)}%`;
        },
        formatCurrency(cents) {
            return new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
            }).format(Number(cents || 0) / 100);
        },
        metricClass(value, dangerAt, warnAt) {
            const number = Number(value || 0);

            if (number >= dangerAt) {
                return "text-red-600 dark:text-red-400";
            }

            if (number >= warnAt) {
                return "text-yellow-600 dark:text-yellow-400";
            }

            return "text-gray-900 dark:text-white";
        },
    },
};
</script>
